import { AggregationPeriod } from '@/pages/OperationsInsights/types/Interval';
import {
    computed, ref, Ref, watch
} from 'vue';
import { useLogger } from '@agyletime/vue-3-common/src/composables/logger';
import { ApiQueryParams } from '@/pages/OperationsInsights/types/ApiQueryParams';
import { fetchBacklogMetricsByWorkstreams } from '@/pages/OperationsInsights/services/fetchBacklogMetricsByWorkstreams';
import { useWorkstreamDefinitionsStore } from '@agyletime/vue-3-common/src/composables/workstreamDefinitionsStore';
import { EnrichedBacklogMetricsByWorkstream } from '@/pages/OperationsInsights/types/BacklogMetricsResponseEnriched';
import { tfCsBLInOutByWorkstreamMetrics } from '@/pages/OperationsInsights/composables/transformations/tfCsBLInOutByWorkstreamMetrics';
import { tfCsBLOpenByWorkstream } from '@/pages/OperationsInsights/composables/transformations/tfCsBLOpenByWorkstream';
import {
    getBLHealthByWorkstreamColumnDefs,
    tfGdBLHealthByWorkstream
} from '@/pages/OperationsInsights/composables/transformations/tfGdBLHealthByWorkstream';
import {
    getBLTrendByWorkstreamColumnDefs,
    tfGdBLTrendByWorkstreamGridData
} from '@/pages/OperationsInsights/composables/transformations/tfGdBLTrendByWorkstream';
const { workstreamDefinitionsMap } = useWorkstreamDefinitionsStore();

export function useBLWorkstreamSummaryData(queryParams: Ref<ApiQueryParams>) {
    const logger = useLogger('useWorkstreamSummary');

    const workstreamData = ref<EnrichedBacklogMetricsByWorkstream[]>([]);
    const aggregationPeriod = ref <AggregationPeriod>(); // No default value
    const isLoading = ref(false);
    const error = ref<string | null>(null);

    // High Chart - Backlog In / Out (Average Time To Resolution) by workstream (for a selected period)
    const backlogInOutAttrByWorkstreamChartXCategoriesSeries = computed(() => {
        return (workstreamData.value ? tfCsBLInOutByWorkstreamMetrics(workstreamData.value) : { xAxisCategories: [], chartSeries: [] });
    });

    // High Chart - Backlog by Workstream (for the last interval)
    const backlogOpenByWorkstreamChartXCategoriesSeries = computed(() => {
        return (workstreamData.value ? tfCsBLOpenByWorkstream(workstreamData.value) : { xAxisCategories: [], chartSeries: [] });
    });

    // Grid Data and Column Def for (backlog health by workstream)
    const backlogHealthByWorkstreamGridData = computed(() => {
        return (workstreamData.value ? tfGdBLHealthByWorkstream(workstreamData.value) : []);
    });

    const backlogHealthByWorkstreamColumnDefs = computed(() => {
        return getBLHealthByWorkstreamColumnDefs();
    });

    // Grid Data and Column Def for (backlog trend by workstream)
    const backlogTrendByWorkstreamGridData = computed(() => {
        return workstreamData.value ? tfGdBLTrendByWorkstreamGridData(workstreamData.value, aggregationPeriod.value) : [];
    });

    const backlogTrendByWorkstreamColumnDefs = computed(() => {
        return getBLTrendByWorkstreamColumnDefs();
    });

    // Grid Column Def and Data (Workstream Summary)
    const commColDef = {
        dataType: 'number', filter: true, flex: 1
    };
    const workstreamSummaryColumnDefs = ref([
        {
            id: 'name', field: 'name', colId: 'name', headerName: 'Name', dataType: 'string', filter: true, flex: 2
        },
        {
            id: 'openWithinSLABoP', field: 'openWithinSLABoP', headerName: 'Open (BoP)', ...commColDef
        },
        {
            id: 'openWithinSLAEoP', field: 'openWithinSLAEoP', headerName: 'Open (EoP)', ...commColDef
        },
        {
            id: 'openAtRiskBoP', field: 'openAtRiskBoP', headerName: 'Open at Risk (BoP)', ...commColDef
        },
        {
            id: 'openAtRiskEoP', field: 'openAtRiskEoP', headerName: 'Open at Risk (EoP)', ...commColDef
        },
        {
            id: 'openBreachedBoP', field: 'openBreachedBoP', headerName: 'Open Breached (BoP)', ...commColDef
        },
        {
            id: 'openBreachedEoP', field: 'openBreachedEoP', headerName: 'Open Breached (EoP)', ...commColDef
        },
        {
            id: 'closedWithinSLA', field: 'closedWithinSLA', headerName: 'Completed in SLA', ...commColDef
        },
        {
            id: 'closedBreached', field: 'closedBreached', headerName: 'Completed Breached', ...commColDef
        },
        {
            id: 'averageResolutionTimeInHour', field: 'averageResolutionTimeInHour', headerName: 'Avg Time to Completion (Hr)', ...commColDef
        },
        {
            id: 'averageHandleTimeSecs', field: 'averageHandleTimeSecs', headerName: 'Avg Completion Handle time (Secs)', ...commColDef
        }
    ]);

    const workstreamSummaryGridData = computed(() => {
        return workstreamData.value ? workstreamData.value : [];
    });

    const fetchData = async () => {
        logger.debug('fetch data');
        if (!queryParams.value) return;
        if (!queryParams.value.workstreamDefinitionIds?.length) return;

        isLoading.value = true;
        error.value = null;

        try {
            // store metrics
            const apiResp = await fetchBacklogMetricsByWorkstreams(queryParams.value);
            workstreamData.value = apiResp?.metrics?.length ? apiResp.metrics.map((metric) => {
                const name = workstreamDefinitionsMap.get(metric.workstreamDefinitionId)?.name ?? `Unknown (${metric.workstreamDefinitionId})`;
                return {
                    name,
                    ...metric
                };
            }) : [];
            // Since the metrics are rolled up to a summary, store the aggregation period separately
            aggregationPeriod.value = apiResp.aggregationPeriod;
        } catch (err) {
            error.value = 'Failed to fetch workstream summary data';
            logger.error('Failed to fetch workstream summary data', { error: err });
        } finally {
            isLoading.value = false;
        }
    };

    // Watch queryParams and fetch data when they change
    watch(queryParams, fetchData, { immediate: true, deep: true });

    const domLayout = 'autoHeight';

    return {
        backlogOpenByWorkstreamChartXCategoriesSeries,
        backlogInOutAttrByWorkstreamChartXCategoriesSeries,
        backlogHealthByWorkstreamColumnDefs,
        backlogHealthByWorkstreamGridData,
        backlogTrendByWorkstreamColumnDefs,
        backlogTrendByWorkstreamGridData,
        workstreamSummaryColumnDefs,
        workstreamSummaryGridData,
        isLoading,
        error,
        fetchData,
        domLayout
    };
}